import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

import { useAuth } from '../../contexts/AuthContext';

dayjs.locale('fr');

const ResourceHeader = ({ name }) => {
    const displayName = (name || '').trim();

    const initials = displayName
        ? displayName
            .split(' ')
            .filter(Boolean)
            .map((s) => s[0])
            .join('')
            .slice(0, 2)
            .toUpperCase()
        : '?';

    return (
        <div
            title={displayName}
            className="flex justify-center items-center bg-gray-600 shadow-sm rounded-full ring-2 ring-gray-500 w-8 h-8 font-semibold text-white text-sm"
        >
            {initials}
        </div>
    );
};

const isFakeRdv = (rdv) => rdv.Titre === 'Aucun rendez-vous à venir';

function ListRDV({ lstRdv = [], loading,onCountChange,  onVisibleRdvChange,      // NEW
  defaultShowOnlyMine = false, // NEW
   }) {
    const navigate = useNavigate();
    const { user } = useAuth();

    const [currentPage, setCurrentPage] = useState(1);
const [showOnlyMine, setShowOnlyMine] = useState(defaultShowOnlyMine);
const [visibleRdv, setVisibleRdv] = useState([]);

    const [sortConfig, setSortConfig] = useState({
        key: 'date',
        direction: 'asc',
    });

    const itemsPerPage = 5;
    const columnsCount = 4;

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return {
                    key,
                    direction: prev.direction === 'asc' ? 'desc' : 'asc',
                };
            }
            return {
                key,
                direction: 'asc',
            };
        });
        setCurrentPage(1);
    };

    const getDateTime = (rdv) => {
        const base = dayjs(rdv.Date_Fix);
        if (!base.isValid()) return dayjs(0);

        const rawTime =
            rdv.Heure_Fix && rdv.Heure_Fix !== 'Non Renseignée'
                ? rdv.Heure_Fix
                : '00:00';

        const [hStr, mStr] = rawTime.split(':');
        const h = parseInt(hStr, 10) || 0;
        const m = parseInt(mStr, 10) || 0;

        return base.hour(h).minute(m);
    };

    // 🔹 Tri + filtre dans un seul useMemo pour éviter les surprises
const processedRdv = useMemo(() => {
  if (!Array.isArray(lstRdv)) return [];

  const real = lstRdv.filter((rdv) => !isFakeRdv(rdv));
  const fake = lstRdv.filter((rdv) => isFakeRdv(rdv));

  let sortedReal = [...real];

  sortedReal.sort((a, b) => {
    if (sortConfig.key === 'date') {
      const tA = getDateTime(a).valueOf();
      const tB = getDateTime(b).valueOf();
      if (tA < tB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (tA > tB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    }

    if (sortConfig.key === 'ca') {
      const caA = (a.CA || '').toLowerCase();
      const caB = (b.CA || '').toLowerCase();
      if (caA < caB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (caA > caB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    }

    return 0;
  });

  let result = [...sortedReal, ...fake];

  if (showOnlyMine && user?.Nom) {
    const normalize = (str) =>
      (str || '').toString().trim().toLowerCase();

    const userName = normalize(user.Nom);

    result = result.filter((rdv) => {
      const caName = normalize(rdv.CA);
      return caName.includes(userName);
    });
  }

  return result;
}, [lstRdv, sortConfig, showOnlyMine, user?.Nom]);


    useEffect(() => {
    if (typeof onCountChange === 'function') {
        const realCount = processedRdv.filter((rdv) => !isFakeRdv(rdv)).length;
        onCountChange(realCount);
    }
}, [processedRdv, onCountChange]);

useEffect(() => {
  setVisibleRdv(processedRdv);
  if (onVisibleRdvChange) {
    onVisibleRdvChange(processedRdv);
  }
}, [processedRdv, onVisibleRdvChange]);

    const handleRowClick = (uuid) => {
        if (uuid) navigate(`/declarations/${uuid}`);
    };

const totalPages = Math.ceil(visibleRdv.length / itemsPerPage) || 1;
const indexOfLastItem = currentPage * itemsPerPage;
const indexOfFirstItem = indexOfLastItem - itemsPerPage;
const currentItems = visibleRdv.slice(indexOfFirstItem, indexOfLastItem);


    return (
        <div className="mb-4">
            <div className="flex justify-end mb-4">
                <label className="flex items-center gap-2 text-lg cursor-pointer mcui-checkbox">
<input
  type="checkbox"
  className="w-4 h-4 accent-orange-500"
  checked={showOnlyMine}
  onChange={(e) => {
    setShowOnlyMine(e.target.checked);
    setCurrentPage(1);
  }}
/>

                    <div>
                        <svg
                            className="mcui-check"
                            viewBox="-2 -2 35 35"
                            aria-hidden="true"
                        >
                            <polyline points="7.57 15.87 12.62 21.07 23.43 9.93" />
                        </svg>
                    </div>
                    <span >Afficher uniquement mes RDV</span>
                </label>
            </div>

            <div className="max-w-full overflow-x-auto">
                <div className="rounded-xl projects">
                    <table className="projects-table w-full min-w-[800px]">
                        <thead>
                            <tr className="*:text-sm">
                                <th>Titre</th>

                                <th
                                    className="text-center cursor-pointer select-none"
                                    onClick={() => handleSort('date')}
                                >
                                    Date de RDV{' '}
                                    {sortConfig.key === 'date' &&
                                        (sortConfig.direction === 'asc'
                                            ? '↑'
                                            : '↓')}
                                </th>

                                <th
                                    className="cursor-pointer select-none"
                                    onClick={() => handleSort('ca')}
                                >
                                    Charges Affaires{' '}
                                    {sortConfig.key === 'ca' &&
                                        (sortConfig.direction === 'asc'
                                            ? '↑'
                                            : '↓')}
                                </th>

                                <th>Site</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                Array.from({ length: itemsPerPage }).map(
                                    (_, index) => (
                                        <tr
                                            key={`skeleton-${index}`}
                                            className="h-16 text-center"
                                        >
                                            {Array.from({
                                                length: columnsCount,
                                            }).map((_, colIdx) => (
                                                <td key={colIdx}>
                                                    <div className="bg-gray-700/30 mx-auto rounded-md w-3/4 h-4 animate-pulse"></div>
                                                </td>
                                            ))}
                                        </tr>
                                    )
                                )
                            ) : (
                                <>
                                    {currentItems.map((rdv, index) => (
                                        <tr
                                            key={index}
                                            className="hover:bg-gray-100 h-16 text-center cursor-pointer"
                                            onClick={() =>
                                                !isFakeRdv(rdv) &&
                                                handleRowClick(
                                                    rdv.UUID_Declaration
                                                )
                                            }
                                        >
                                            <td>
                                                <p>{rdv.Titre}</p>
                                                <p>
                                                    {!isFakeRdv(rdv) &&
                                                        (rdv.DeclarationLibelle
                                                            ?.length > 20
                                                            ? `${rdv.DeclarationLibelle.slice(
                                                                  0,
                                                                  20
                                                              )}…`
                                                            : rdv.DeclarationLibelle)}
                                                </p>
                                            </td>

                                            <td className="text-center">
                                                {!isFakeRdv(rdv) ? (
                                                    <div className="flex justify-center items-center">
                                                        <p className="inline-block text-white">
                                                            <span className="block font-semibold text-[24px] text-white text-center leading-[0.7]">
                                                                {dayjs(
                                                                    rdv.Date_Fix
                                                                ).format('DD')}
                                                            </span>
                                                            <span className="block text-[14px] text-gray-300">
                                                                {dayjs(
                                                                    rdv.Date_Fix
                                                                )
                                                                    .format(
                                                                        'MMMM YYYY'
                                                                    )
                                                                    .replace(
                                                                        /^\w/,
                                                                        (c) =>
                                                                            c.toUpperCase()
                                                                    )}
                                                            </span>
                                                        </p>
                                                        {rdv.Heure_Fix !==
                                                            'Non Renseignée' && (
                                                            <>
                                                                <span className="inline-block mx-2 text-gray-300 text-sm">
                                                                    à
                                                                </span>
                                                                <span className="inline-block text-white text-base">
                                                                    {
                                                                        rdv.Heure_Fix
                                                                    }
                                                                </span>
                                                            </>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div>-</div>
                                                )}
                                            </td>

                                            <td className="member">
                                                {!isFakeRdv(rdv) ? (
                                                    <div className="flex justify-center items-center gap-3">
                                                        <div className="shrink-0">
                                                            {rdv.CA === '' ||
                                                            rdv.CA == null ? (
                                                                <img
                                                                    src="https://cdn-icons-png.flaticon.com/512/149/149071.png"
                                                                    alt="Aucun Chargé d'affaires"
                                                                    className="rounded-full w-10 h-10 object-cover"
                                                                />
                                                            ) : (
                                                                <ResourceHeader
                                                                    name={rdv.CA}
                                                                />
                                                            )}
                                                        </div>

                                                        <div className="text-left member-info">
                                                            <p className="font-medium text-white text-sm">
                                                                {rdv.CA === '' ||
                                                                rdv.CA == null
                                                                    ? 'Aucun'
                                                                    : rdv.CA}
                                                            </p>
                                                            <p className="text-gray-400 text-xs">
                                                                Chargé
                                                                d'affaires
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div>-</div>
                                                )}
                                            </td>

                                            <td>
                                                {!isFakeRdv(rdv) ? (
                                                    <>
                                                    <p className="font-medium text-white text-sm">
                                                        {rdv.SiteNom}
                                                    </p>
                                                    <p className="text-gray-400 text-xs"></p>
                                                    </>
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                        </tr>
                                    ))}

                                    {Array.from({
                                        length: Math.max(
                                            0,
                                            itemsPerPage - currentItems.length
                                        ),
                                    }).map((_, index) => (
                                        <tr
                                            key={`empty-${index}`}
                                            className="opacity-0 h-16 text-center pointer-events-none select-none"
                                        >
                                            {Array.from({
                                                length: columnsCount,
                                            }).map((_, colIdx) => (
                                                <td key={colIdx}>
                                                    <p>.</p>
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {totalPages > 1 && (
                <div className="flex justify-center items-center space-x-4 mt-6">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`w-10 h-10 rounded-full font-semibold text-sm border border-[#313D4E] transition duration-300
                                    ${
                                        page === currentPage
                                            ? 'bg-orange-500 text-white shadow-md'
                                            : 'bg-[#273142] text-white hover:bg-[#313D4E]'
                                    }`}
                            >
                                {page}
                            </button>
                        )
                    )}
                </div>
            )}
        </div>
    );
}

export default ListRDV;
